import React from 'react';
import { useTranslation } from 'react-i18next';

const Footer = ({ t }) => {
    useTranslation(); // re-render on language change

    const quickLinks = [
        { href: '#hero', label: t('nav-home') },
        { href: '#about', label: t('nav-about') },
        { href: '#blog', label: t('nav-blog') },
        { href: '#contact', label: t('nav-contact') },
    ];

    const services = [
        t('services-weight-loss'),
        t('services-nutrition'),
        t('services-meal-planning'),
        t('services-health-coaching'),
        t('services-personal-training'),
    ];

    const socials = [
        { icon: 'fab fa-facebook-f', href: 'https://facebook.com', key: 'social-facebook' },
        { icon: 'fab fa-instagram', href: 'https://www.instagram.com/diet.sarakhedr/', key: 'social-instagram' },
        { icon: 'fab fa-youtube', href: 'https://youtube.com', key: 'social-youtube' },
    ];

    return (
        <footer className="bg-primary-deep text-white">
            <div className="sk-container py-14">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
                    {/* Brand */}
                    <div>
                        <div className="flex items-center gap-3">
                            <img
                                src="/sk-clinic-logo.png"
                                alt=""
                                width="48"
                                height="48"
                                loading="lazy"
                                className="w-12 h-12 rounded-full bg-white p-1"
                            />
                            <span>
                                <span className="block font-heading font-bold text-[1.2rem]">Sara Khedr</span>
                                <span className="block text-[0.8rem] text-white/60">{t('nav-subtitle')}</span>
                            </span>
                        </div>
                        <p className="mt-4 text-white/70 text-[0.92rem] leading-relaxed max-w-[38ch]">
                            {t('footer-description')}
                        </p>
                        <div className="flex gap-2.5 mt-5">
                            {socials.map((s) => (
                                <a
                                    key={s.key}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={t(s.key)}
                                    className="flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 text-white transition-all duration-200 hover:bg-secondary hover:-translate-y-0.5"
                                >
                                    <i className={s.icon} aria-hidden="true" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick links */}
                    <nav aria-label={t('footer-quick-links')}>
                        <h3 className="font-heading font-semibold !text-white text-[0.95rem] uppercase tracking-wider mb-4">
                            {t('footer-quick-links')}
                        </h3>
                        <ul className="list-none m-0 p-0 flex flex-col gap-2.5">
                            {quickLinks.map((link) => (
                                <li key={link.href}>
                                    <a
                                        href={link.href}
                                        className="text-white/70 text-[0.92rem] transition-colors duration-200 hover:text-secondary"
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    {/* Services */}
                    <div>
                        <h3 className="font-heading font-semibold !text-white text-[0.95rem] uppercase tracking-wider mb-4">
                            {t('services-title')}
                        </h3>
                        <ul className="list-none m-0 p-0 flex flex-col gap-2.5">
                            {services.map((service) => (
                                <li key={service} className="text-white/70 text-[0.92rem] leading-snug">
                                    {service}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h3 className="font-heading font-semibold !text-white text-[0.95rem] uppercase tracking-wider mb-4">
                            {t('contact-info-title')}
                        </h3>
                        <ul className="list-none m-0 p-0 flex flex-col gap-3">
                            <li className="flex items-center gap-2.5 text-white/70 text-[0.92rem]">
                                <i className="fas fa-phone-alt text-secondary" aria-hidden="true" />
                                <span dir="ltr">{t('contact-phone-number')}</span>
                            </li>
                            <li className="flex items-center gap-2.5 text-white/70 text-[0.92rem]">
                                <i className="fas fa-envelope text-secondary" aria-hidden="true" />
                                <span dir="ltr">{t('contact-email-address')}</span>
                            </li>
                            <li className="flex items-start gap-2.5 text-white/70 text-[0.92rem]">
                                <i className="fas fa-map-marker-alt text-secondary mt-1" aria-hidden="true" />
                                {t('contact-location-address')}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Legal bar */}
            <div className="border-t border-white/10">
                <div className="sk-container py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="m-0 text-white/50 text-[0.82rem] text-center sm:text-start">
                        {t('footer-copyright')}
                    </p>
                    <ul className="list-none m-0 p-0 flex items-center gap-5">
                        {[t('footer-privacy'), t('footer-terms'), t('footer-cookies')].map((label) => (
                            <li key={label}>
                                <a href="#hero" className="text-white/50 text-[0.82rem] transition-colors duration-200 hover:text-secondary">
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
